export type ChatbotFilterValue =
  | string
  | number
  | boolean
  | null
  | ReadonlyArray<ChatbotFilterValue>
  | { [key: string]: ChatbotFilterValue };

export interface ChatbotActiveFilters {
  globalFilters?: Record<string, ChatbotFilterValue>;
  localFilters?: Record<string, ChatbotFilterValue>;
  resolvedCountryCodes?: string[];
}

export interface ChatbotMessageFilter {
  label: string;
  value: string;
  details?: string;
}

export interface ChatbotSessionRequest {
  message: string;
  active_filters?: ChatbotActiveFilters;
  conversation_id?: string;
  page_url?: string;
}

export interface ChatbotSessionResponse {
  session_id: string;
  stream_url: string;
  conversation_id?: string;
}

export interface ChatbotStopRequest {
  conversation_id: string;
  session_id?: string;
}

export interface ChatbotTextEvent {
  text: string;
}

export interface ChatbotFileEvent {
  type: 'file';
  url: string;
  filename: string;
  size_bytes: number;
  content_type?: string;
}

export interface ChatbotReasoningEvent {
  type: 'reasoning';
  text: string;
}

export interface ChatbotErrorEvent {
  error: string;
}

export type ChatbotSSEEvent = ChatbotTextEvent | ChatbotFileEvent | ChatbotReasoningEvent | ChatbotErrorEvent;

export type ChatbotInterruptedVariant = 'live' | 'history';

export interface ChatbotMessage {
  id: string;
  role: 'user' | 'ai';
  content: string;
  timestamp: number;
  files?: ChatbotFileEvent[];
  promptFilters?: ReadonlyArray<ChatbotMessageFilter>;
  reasoningSteps?: string[];
  interrupted?: ChatbotInterruptedVariant;
}

export interface ChatbotFilterChipGroups {
  globalFilters: ReadonlyArray<ChatbotMessageFilter>;
  localFilters: ReadonlyArray<ChatbotMessageFilter>;
}

export interface ChatbotRateLimit {
  remaining: number | null;
  limit: number | null;
  resetAt: number | null;
}

export interface ChatbotConversation {
  id: string;
  title: string;
  created_at?: number;
  updated_at?: number;
  message_count?: number;
  active_filters?: ChatbotActiveFilters;
}

export interface ChatbotHistoryMessageFile {
  s3_key: string;
  filename: string;
  size_bytes: number;
  url: string;
}

export interface ChatbotTaskPlanEntry {
  id: number;
  mode: string;
  label: string;
}

export interface ChatbotHistoryMessage {
  role: 'user' | 'ai' | 'assistant';
  content: string;
  timestamp?: number;
  files?: ChatbotHistoryMessageFile[] | null;
  reasoning_steps?: string[] | null;
  task_plan?: ChatbotTaskPlanEntry[] | null;
}

export interface ChatbotConversationListResponse {
  conversations: ChatbotConversation[];
  next_token?: string | null;
}

export interface ChatbotConversationDetailResponse {
  conversation: ChatbotConversation;
  messages: ChatbotHistoryMessage[];
}

export interface ChatbotConversationRenameResponse {
  conversation: { id: string; title: string };
}

export const CHATBOT_DONE_MARKER = '[DONE]';

export const CHATBOT_STOPPED_MARKER = '*Response canceled*';
